import { cn } from '@/lib/utils';
import {
  STATUS_ORDER,
  STATUS_CONFIG,
  type TestCaseStatus,
} from '@/lib/status-config';
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from '@/components/ui/tooltip';

interface StatusBarProps {
  counts: Record<TestCaseStatus, number>;
  total: number;
  className?: string;
  height?: string;
}

/**
 * Stacked horizontal progress bar showing all 6 statuses proportionally.
 * Tooltip on each segment shows count and percentage.
 */
export function StatusBar({
  counts,
  total,
  className,
  height = 'h-2',
}: StatusBarProps) {
  if (total === 0) {
    return (
      <div className={cn('w-full rounded-full bg-muted', height, className)} />
    );
  }

  return (
    <TooltipProvider>
      <div
        className={cn(
          'flex w-full overflow-hidden rounded-full bg-muted',
          height,
          className
        )}
      >
        {STATUS_ORDER.map((status) => {
          const count = counts[status] || 0;
          if (count === 0) return null;
          const pct = (count / total) * 100;
          const config = STATUS_CONFIG[status];

          return (
            <Tooltip key={status}>
              <TooltipTrigger asChild>
                <div
                  className={cn('transition-all', config.barClass)}
                  style={{ width: `${pct}%` }}
                />
              </TooltipTrigger>
              <TooltipContent>
                <p className="text-xs font-medium">
                  {config.label}: {count} ({pct.toFixed(1)}%)
                </p>
              </TooltipContent>
            </Tooltip>
          );
        })}
      </div>
    </TooltipProvider>
  );
}
